import {
  AddRounded,
  ArrowBack,
  CheckCircleRounded,
  CheckRounded,
  Close,
  FileDownloadOutlined,
  PaidOutlined,
  PostAddOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { fetchInvoicePdf, fetchInvoices } from "@/api/invoices";
import RelatedJournalEntries from "@/components/accounting/RelatedJournalEntries";
import Blotter, { type BlotterConfig, type BlotterData } from "@/components/Blotter";
import type { AppliedFilter, FilterField } from "@/components/BlotterFilterBar";
import RecordPayoutDrawer from "@/components/finance/RecordPayoutDrawer";
import StatusBadge, { type StatusTone } from "@/components/StatusBadge";
import type { AddressFormValues } from "@/components/usersOrgs/AddressForm";
import InvoiceReceipts from "@/components/usersOrgs/InvoiceReceipts";
import RecordPaymentDrawer from "@/components/usersOrgs/RecordPaymentDrawer";
import { SOURCE_TYPE_INVOICE } from "@/constants/accounting";
import { DATE_INPUT_LANG } from "@/constants/dates";
import {
  useArchiveBillingAddress,
  useBillingAddresses,
  useCreateBillingAddress,
} from "@/hooks/useBillingAddresses";
import { useCollections } from "@/hooks/useCollections";
import { useCreditSummary } from "@/hooks/useCredits";
import {
  useAddInvoiceLine,
  useCancelInvoice,
  useCreateInvoice,
  useFinalizeInvoice,
  useGroupInvoiceCreators,
  useInvoice,
  useInvoiceEvents,
  useInvoices,
  useProvisionInvoice,
  useRecordInvoicePayout,
  useRemoveInvoiceLine,
  useUpdateInvoice,
  useVoidInvoice,
} from "@/hooks/useInvoices";
import { usePermissions } from "@/hooks/usePermissions";
import type { BillingAddress } from "@/interfaces/billingAddresses";
import type {
  ExpectedPaymentMethod,
  Invoice,
  InvoiceEvent,
  InvoiceLineItem,
  InvoiceWithLines,
} from "@/interfaces/invoices";
import {
  EXPECTED_PAYMENT_METHOD,
  INVOICE_STATUS,
  INVOICE_STATUS_HELP,
  INVOICE_STATUS_LABEL,
  INVOICE_STATUS_PARAM,
  LINE_KIND,
} from "@/interfaces/invoices";

// Lazy-loaded: the address form pulls in country-state-city (~130 KB gzipped), so it's code-split
// and only fetched when a user actually opens "Add new address" — zero cost to the main bundle.
const AddressForm = lazy(() => import("@/components/usersOrgs/AddressForm"));

const money = (v: string | null | undefined, ccy = "usd") =>
  v == null
    ? "—"
    : `${ccy.toUpperCase()} ${Number(v).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
const shortDate = (iso: string | null) => (iso ? iso.slice(0, 10) : "—");
/** "Jun 12 2026, 3:04 PM" — used in the activity log. */
const dateTime = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";

/** "Jun 26 2026" — unambiguous US format, independent of browser locale. */
const usDate = (iso: string) =>
  iso
    ? new Date(`${iso.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
        timeZone: "UTC",
      })
    : "";

/**
 * Due-date field that always DISPLAYS the US format (e.g. "Jun 26 2026") — a native `<input type=date>`
 * renders in the browser's locale (dd/mm/yyyy for many), which we can't control. We show the formatted
 * value in a read-only field and open the native date picker via showPicker() on click.
 */
function DueDateField({ value, onChange }: { value: string; onChange: (iso: string) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const dateOnly = (value ?? "").slice(0, 10);
  const openPicker = () => {
    const el = ref.current;
    if (!el) return;
    if (typeof el.showPicker === "function") el.showPicker();
    else el.focus();
  };
  return (
    <Box sx={{ position: "relative" }}>
      <TextField
        label="Due date"
        value={usDate(dateOnly)}
        onClick={openPicker}
        size="small"
        fullWidth
        placeholder="Select a date"
        slotProps={{ inputLabel: { shrink: true }, input: { readOnly: true } }}
        helperText="Defaults to issue + net terms; edit if needed."
        sx={{ "& input": { cursor: "pointer" } }}
      />
      {/* Hidden native picker — overlays the field so showPicker() anchors correctly; clicks pass
          through to the TextField above. */}
      <input
        ref={ref}
        type="date"
        lang={DATE_INPUT_LANG}
        value={dateOnly}
        onChange={(e) =>
          e.target.value && onChange(new Date(`${e.target.value}T12:00:00Z`).toISOString())
        }
        tabIndex={-1}
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          opacity: 0,
          pointerEvents: "none",
        }}
      />
    </Box>
  );
}

const STATUS_COLOR: Record<number, "default" | "info" | "warning" | "success" | "error"> = {
  [INVOICE_STATUS.draft]: "default", // gray
  [INVOICE_STATUS.pending]: "info", // blue
  [INVOICE_STATUS.partial_payment]: "warning", // amber (active state; not in the requested scheme)
  [INVOICE_STATUS.paid]: "success", // green
  [INVOICE_STATUS.overdue]: "error", // red
  [INVOICE_STATUS.fail_hold]: "warning", // amber — payment attempt failed, awaiting retry (credits held)
  [INVOICE_STATUS.clearing]: "success", // green — settled at Stripe (cash in Stripe, awaiting bank)
  [INVOICE_STATUS.stripe_retry]: "warning", // amber — payout bounced, Stripe re-issuing (money not lost)
  [INVOICE_STATUS.cancelled]: "default", // transparent — see TRANSPARENT_STATUSES
  [INVOICE_STATUS.voided]: "default", // gray (not in the requested scheme)
};

// Rendered borderless + muted so they read as "transparent", distinct from draft's gray chip.
const TRANSPARENT_STATUSES = new Set<number>([INVOICE_STATUS.cancelled]);

export function StatusChip({ status }: { status: number }) {
  const help = INVOICE_STATUS_HELP[status];
  // Cancelled is over and carries no signal, so it recedes rather than competing with live states.
  const tone: StatusTone = TRANSPARENT_STATUSES.has(status)
    ? "muted"
    : (STATUS_COLOR[status] ?? "default") === "default"
      ? "neutral"
      : (STATUS_COLOR[status] as StatusTone);
  const chip = <StatusBadge label={String(INVOICE_STATUS_LABEL[status] ?? status)} tone={tone} />;
  return help ? (
    <Tooltip title={help} arrow>
      <span>{chip}</span>
    </Tooltip>
  ) : (
    chip
  );
}

const creatorName = (i: Invoice) => i.createdByName ?? "—";

/** Stripe-style quick-filter pills for status. `null` = All. Clicking selects that status. */
const STATUS_PILLS: { label: string; value: number | null }[] = [
  { label: "All", value: null },
  ...Object.entries(INVOICE_STATUS_LABEL).map(([value, label]) => ({
    label,
    value: Number(value),
  })),
];

function StatusPills({
  active,
  onChange,
}: {
  active: number | null;
  onChange: (v: number | null) => void;
}) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
      {STATUS_PILLS.map((p) => {
        const isActive = active === p.value;
        const help = p.value == null ? null : INVOICE_STATUS_HELP[p.value];
        const pill = (
          <Button
            key={p.label}
            size="small"
            onClick={() => onChange(p.value)}
            sx={{
              textTransform: "none",
              borderRadius: 999,
              px: 1.75,
              border: "1px solid",
              borderColor: isActive ? "var(--color-rodeo-black)" : "var(--color-smoke)",
              bgcolor: isActive ? "var(--color-rodeo-black)" : "transparent",
              color: isActive ? "var(--color-white)" : "var(--color-graphite)",
              "&:hover": {
                bgcolor: isActive ? "var(--color-rodeo-black)" : "var(--color-cloud, #f2f0ec)",
              },
            }}
          >
            {p.label}
          </Button>
        );
        return help ? (
          <Tooltip key={p.label} title={help} arrow>
            {pill}
          </Tooltip>
        ) : (
          pill
        );
      })}
    </Box>
  );
}

// --- create dialog --------------------------------------------------------------------------

function CreateInvoiceDialog({
  groupId,
  open,
  onClose,
  onCreated,
}: {
  groupId: number;
  open: boolean;
  onClose: () => void;
  onCreated: (invoiceId: number) => void;
}) {
  const create = useCreateInvoice(groupId);
  const [method, setMethod] = useState<ExpectedPaymentMethod>("ach");
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  function submit() {
    setError(null);
    create.mutate(
      {
        expectedPaymentMethod: method,
        description: description || null,
        billing: email ? { email } : undefined,
      },
      {
        onSuccess: (res) => {
          onClose();
          setEmail("");
          setDescription("");
          onCreated(res.invoiceId);
        },
        onError: (e) => setError(e instanceof Error ? e.message : "Couldn't create the invoice."),
      },
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>Send Invoice — new draft</DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: "12px !important" }}
      >
        <TextField
          select
          label="Expected payment method"
          value={method}
          onChange={(e) => setMethod(e.target.value as ExpectedPaymentMethod)}
          size="small"
        >
          {EXPECTED_PAYMENT_METHOD.map((m) => (
            <MenuItem key={m} value={m}>
              {m.toUpperCase()}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Bill-to email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          size="small"
          helperText="Stripe emails the hosted link here when you finalize."
        />
        <TextField
          label="Internal note"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          size="small"
          multiline
          helperText="Shown to your team on the blotter — not sent to the client."
        />
        {error && (
          <Typography sx={{ color: "var(--color-red, #c00)", fontSize: 13 }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={create.isPending}>
          {create.isPending ? "Creating…" : "Create draft"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// --- stat block (wallet-style) --------------------------------------------------------------

function StatTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Box sx={{ minWidth: 96 }}>
      <Typography
        sx={{
          fontSize: 11,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          fontWeight: 600,
          color: "var(--color-tan, #a98d6b)",
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 24, fontWeight: 500, color: "var(--color-rodeo-black)" }}>
        {value}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: 12, color: "var(--color-graphite)", mt: -0.25 }}>
          {sub}
        </Typography>
      )}
    </Box>
  );
}
const Sep = () => (
  <Box sx={{ width: "1px", alignSelf: "stretch", bgcolor: "var(--color-smoke)", mx: 1 }} />
);

function InvoiceStatBlock({ inv }: { inv: InvoiceWithLines }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
        bgcolor: "var(--color-white)",
        border: "1px solid var(--color-smoke)",
        borderRadius: "12px",
        p: "22px 30px",
        mb: 3,
      }}
    >
      <StatTile label="Total" value={money(inv.totalAmount, inv.currency)} />
      <Sep />
      <StatTile label="Paid" value={money(inv.paidAmount, inv.currency)} />
      <Sep />
      <StatTile label="Remaining" value={money(inv.netRemaining, inv.currency)} />
      <Sep />
      <StatTile
        label="Provisioned"
        value={
          inv.isMinted && inv.creditsToMint != null
            ? `${Number(inv.creditsToMint).toLocaleString("en-US")} credits`
            : "0"
        }
        sub={inv.isMinted && inv.mintedAt ? shortDate(inv.mintedAt) : undefined}
      />
      {inv.dueDate && (
        <>
          <Sep />
          <StatTile label="Due" value={shortDate(inv.dueDate)} />
        </>
      )}
    </Box>
  );
}

// --- bill-to (default + change picker) ------------------------------------------------------

function formatAddress(a: {
  contactName: string | null;
  email: string | null;
  addressLine1: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
}): string {
  return [a.addressLine1, [a.city, a.state].filter(Boolean).join(", "), a.postalCode]
    .filter(Boolean)
    .join(" · ");
}

function BillToSection({
  groupId,
  inv,
  editable,
}: {
  groupId: number;
  inv: InvoiceWithLines;
  editable: boolean;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const update = useUpdateInvoice(groupId, inv.id);

  const hasBillTo = inv.contactName || inv.email || inv.addressLine1;
  return (
    <Box
      sx={{
        border: "1px solid var(--color-smoke)",
        borderRadius: "10px",
        p: 2,
        width: 400,
        maxWidth: "100%",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box>
          <Typography
            sx={{
              fontSize: 11,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              fontWeight: 600,
              color: "var(--color-tan, #a98d6b)",
              mb: 0.5,
            }}
          >
            Bill to
          </Typography>
          {hasBillTo ? (
            <>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {inv.contactName ?? "—"}
              </Typography>
              {inv.email && (
                <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
                  {inv.email}
                </Typography>
              )}
              {inv.addressLine1 && (
                <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
                  {formatAddress(inv)}
                </Typography>
              )}
            </>
          ) : (
            <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
              No bill-to set.
            </Typography>
          )}
        </Box>
        {editable && (
          <Button size="small" onClick={() => setPickerOpen(true)} sx={{ textTransform: "none" }}>
            Change
          </Button>
        )}
      </Box>

      <BillToPicker
        groupId={groupId}
        open={pickerOpen}
        selectedId={inv.billingAddressId}
        onClose={() => setPickerOpen(false)}
        onPick={(id) => {
          update.mutate({ billingAddressId: id });
          setPickerOpen(false);
        }}
      />
    </Box>
  );
}

function BillToPicker({
  groupId,
  open,
  selectedId,
  onClose,
  onPick,
}: {
  groupId: number;
  open: boolean;
  selectedId: number | null;
  onClose: () => void;
  onPick: (id: number) => void;
}) {
  const { data: addresses = [] } = useBillingAddresses(open ? groupId : null);
  const createAddress = useCreateBillingAddress(groupId);
  const archiveAddress = useArchiveBillingAddress(groupId);
  const [adding, setAdding] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<BillingAddress | null>(null);
  const [error, setError] = useState<string | null>(null);

  function saveNew(values: AddressFormValues) {
    setError(null);
    // Send only filled fields (empty → null), so we don't persist blank strings.
    const payload = Object.fromEntries(
      Object.entries(values).map(([k, v]) => [k, v.trim() || null]),
    ) as Record<string, string | null>;
    createAddress.mutate(
      { ...payload, isDefault: addresses.length === 0 },
      {
        onSuccess: (a) => {
          setAdding(false);
          onPick(a.id);
        },
        onError: (e) => setError(e instanceof Error ? e.message : "Couldn't save the address."),
      },
    );
  }

  function removeAddress(a: BillingAddress) {
    setError(null);
    archiveAddress.mutate(a.id, {
      onSuccess: () => setConfirmRemove(null),
      onError: (e) => setError(e instanceof Error ? e.message : "Couldn't remove the address."),
    });
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>Choose a bill-to address</DialogTitle>
      <DialogContent sx={{ pt: "8px !important" }}>
        {error && (
          <Typography sx={{ color: "var(--color-red, #c00)", fontSize: 12.5, mb: 1 }}>
            {error}
          </Typography>
        )}
        {addresses.map((a: BillingAddress) => (
          <Box
            key={a.id}
            onClick={() => onPick(a.id)}
            sx={{
              border: "1px solid",
              borderColor: a.id === selectedId ? "var(--color-rodeo-black)" : "var(--color-smoke)",
              borderRadius: "8px",
              p: 1.5,
              mb: 1,
              cursor: "pointer",
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              "&:hover": { borderColor: "var(--color-rodeo-black)" },
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {a.label ?? a.contactName ?? `Address #${a.id}`}
                {a.isDefault ? "  ·  Default" : ""}
              </Typography>
              <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
                {[a.email, formatAddress(a)].filter(Boolean).join(" · ") || "—"}
              </Typography>
            </Box>
            <IconButton
              size="small"
              aria-label={`Remove ${a.label ?? "address"}`}
              title="Remove"
              // Stop the click reaching the card, which would select the address being removed.
              onClick={(e) => {
                e.stopPropagation();
                setConfirmRemove(a);
              }}
              sx={{ flexShrink: 0 }}
            >
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        ))}
        {!adding ? (
          <Button startIcon={<AddRounded />} onClick={() => setAdding(true)} sx={{ mt: 1 }}>
            Add new address
          </Button>
        ) : (
          <Suspense
            fallback={
              <Typography sx={{ fontSize: 13, color: "var(--color-graphite)", mt: 2 }}>
                Loading address form…
              </Typography>
            }
          >
            <AddressForm
              onSave={saveNew}
              onCancel={() => setAdding(false)}
              isSaving={createAddress.isPending}
            />
          </Suspense>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Dialog
        open={confirmRemove !== null}
        onClose={() => setConfirmRemove(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ pb: 1 }}>Remove this address?</DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
            {confirmRemove?.label ?? confirmRemove?.contactName ?? "This address"}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "var(--color-graphite)", mt: 0.5 }}>
            It stops appearing in the bill-to picker. Invoices already using it keep their bill-to
            details, and the label becomes available again.
          </Typography>
          {confirmRemove?.isDefault && (
            <Typography sx={{ fontSize: 13, color: "var(--color-graphite)", mt: 1 }}>
              This is the group's default — the group will have no default until another address is
              added or set.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRemove(null)}>Cancel</Button>
          <Button
            color="error"
            disabled={archiveAddress.isPending}
            onClick={() => confirmRemove && removeAddress(confirmRemove)}
          >
            {archiveAddress.isPending ? "Removing…" : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

// --- preview drawer (Stripe PDF, or an in-app render for drafts) ----------------------------

function PreviewDrawer({
  groupId,
  open,
  onClose,
  inv,
}: {
  groupId: number;
  open: boolean;
  onClose: () => void;
  inv: InvoiceWithLines;
}) {
  const hasPdf = !!inv.invoicePdf;
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState(false);

  // Fetch the PDF through our same-origin proxy (auth via apiClient), then show the object URL in
  // the iframe — Stripe's cross-origin link can't be embedded directly.
  useEffect(() => {
    if (!open || !hasPdf) {
      setPdfUrl(null);
      return;
    }
    let cancelled = false;
    let url: string | null = null;
    setPdfError(false);
    fetchInvoicePdf(groupId, inv.id)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setPdfUrl(url);
      })
      .catch(() => {
        if (!cancelled) setPdfError(true);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [open, hasPdf, groupId, inv.id]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box
        sx={{
          width: { xs: "100vw", sm: "92vw", md: 900 },
          maxWidth: "100vw",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            borderBottom: "1px solid var(--color-smoke)",
          }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
            Preview — {inv.invoiceNumber ?? "Draft"}
          </Typography>
          <IconButton onClick={onClose} aria-label="Close preview">
            <Close />
          </IconButton>
        </Box>
        {hasPdf ? (
          pdfError ? (
            <Box sx={{ p: 3, color: "var(--color-red, #c00)" }}>
              Couldn't load the PDF.{" "}
              {inv.invoicePdf && (
                <Button href={inv.invoicePdf} target="_blank" rel="noreferrer">
                  Open in Stripe
                </Button>
              )}
            </Box>
          ) : pdfUrl ? (
            // Hide the viewer's toolbar/thumbnail pane and fit the page to width.
            <iframe
              title="Invoice PDF"
              src={`${pdfUrl}#toolbar=0&navpanes=0&view=FitH`}
              style={{ border: "none", flex: 1, width: "100%", background: "var(--color-white)" }}
            />
          ) : (
            <Box sx={{ p: 3, color: "var(--color-graphite)" }}>Loading PDF…</Box>
          )
        ) : (
          // Drafts have no Stripe PDF yet — render an in-app preview of the current state.
          <Box sx={{ p: 4, overflowY: "auto" }}>
            <Typography sx={{ fontSize: 22, fontWeight: 500, mb: 0.5 }}>Invoice (draft)</Typography>
            <Typography sx={{ fontSize: 13, color: "var(--color-graphite)", mb: 3 }}>
              This preview is generated in-app; the final PDF is produced by Stripe on finalize.
            </Typography>
            {(inv.contactName || inv.email) && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    textTransform: "uppercase",
                    color: "var(--color-tan, #a98d6b)",
                  }}
                >
                  Bill to
                </Typography>
                <Typography sx={{ fontSize: 14 }}>{inv.contactName}</Typography>
                <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
                  {inv.email}
                </Typography>
              </Box>
            )}
            <LinesTable inv={inv} editable={false} onRemove={() => {}} />
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

// --- lines table ----------------------------------------------------------------------------

function LinesTable({
  inv,
  editable,
  onRemove,
}: {
  inv: InvoiceWithLines;
  editable: boolean;
  onRemove: (lineId: number) => void;
}) {
  const numSx = { textAlign: "right" as const, whiteSpace: "nowrap" as const };
  const headSx = { fontWeight: 600, bgcolor: "var(--color-white)" };
  return (
    <TableContainer
      sx={{ maxHeight: "48vh", border: "1px solid var(--color-smoke)", borderRadius: "10px" }}
    >
      <Table size="small" stickyHeader sx={{ "& td, & th": { borderColor: "var(--color-smoke)" } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={headSx}>Description</TableCell>
            <TableCell sx={{ ...headSx, ...numSx }}>Qty</TableCell>
            <TableCell sx={{ ...headSx, ...numSx }}>Unit</TableCell>
            <TableCell sx={{ ...headSx, ...numSx }}>Amount</TableCell>
            {editable && <TableCell sx={headSx} />}
          </TableRow>
        </TableHead>
        <TableBody>
          {inv.lineItems.length === 0 && (
            <TableRow>
              <TableCell colSpan={editable ? 5 : 4} sx={{ color: "var(--color-graphite)", py: 3 }}>
                No lines yet — add one above.
              </TableCell>
            </TableRow>
          )}
          {inv.lineItems.map((l: InvoiceLineItem) => {
            const isCredit = l.kind === LINE_KIND.applied_credit;
            const isDiscount = l.kind === LINE_KIND.discount;
            const isNegative = isCredit || isDiscount;
            return (
              <TableRow key={l.id} hover>
                <TableCell>
                  {l.description}
                  {isNegative && (
                    <Chip
                      size="small"
                      label={isDiscount ? "discount" : "credit"}
                      variant="outlined"
                      sx={{ ml: 1, height: 20 }}
                    />
                  )}
                </TableCell>
                <TableCell sx={numSx}>{isNegative ? "—" : (l.quantity ?? "—")}</TableCell>
                <TableCell sx={numSx}>{money(l.unitPrice, inv.currency)}</TableCell>
                <TableCell
                  sx={{ ...numSx, color: isNegative ? "var(--color-red, #c00)" : "inherit" }}
                >
                  {money(l.amount, inv.currency)}
                </TableCell>
                {editable && (
                  <TableCell sx={{ textAlign: "right" }}>
                    <IconButton
                      size="small"
                      aria-label="Remove line"
                      onClick={() => onRemove(l.id)}
                    >
                      <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
        {inv.lineItems.length > 0 && (
          <TableFooter>
            <TableRow>
              <TableCell colSpan={editable ? 3 : 2} />
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: "var(--color-rodeo-black)",
                  textAlign: "right",
                  fontSize: 14,
                }}
              >
                Total {money(inv.totalAmount, inv.currency)}
              </TableCell>
              {editable && <TableCell />}
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
}

// --- add-line form (draft editor) -----------------------------------------------------------

type LineMode = "collection" | "general" | "applied_credit" | "discount";

/** Strips the thousands separators the credit / discount fields are formatted with on blur. */
const unformat = (s: string) => Number(s.replace(/,/g, ""));

function AddLineForm({ groupId, invoiceId }: { groupId: number; invoiceId: number }) {
  const { data: collections = [] } = useCollections();
  const { data: summary } = useCreditSummary(groupId);
  const addLine = useAddInvoiceLine(groupId, invoiceId);
  const [mode, setMode] = useState<LineMode>("collection");
  const [collectionId, setCollectionId] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [quantity, setQuantity] = useState("1");
  // General lines keep their own fields so switching modes can't carry a collection's price onto a
  // free-text line (or the reverse) without the biller noticing.
  const [generalTitle, setGeneralTitle] = useState("");
  const [generalUnitPrice, setGeneralUnitPrice] = useState("");
  const [generalQty, setGeneralQty] = useState("1");
  const [creditInput, setCreditInput] = useState("");
  const [creditWarning, setCreditWarning] = useState<string | null>(null);
  const [discountUsd, setDiscountUsd] = useState("");
  const [error, setError] = useState<string | null>(null);

  const balance = Math.floor(Number(summary?.balance ?? 0)); // max applicable credits
  const creditsPerUsd = summary?.creditsPerUsd ?? 1;
  const generalTotal = unformat(generalUnitPrice) * Number(generalQty);

  /**
   * The pre-add checks on the applied-credit field, in credits. Returns the warning to show under
   * the field, or null when the value may be added. The server caps at the balance too
   * (AppliedCreditsExceedBalanceError) — this is the fast feedback, not the control.
   */
  function validateCredit(raw: string): string | null {
    const n = unformat(raw);
    if (raw.trim() === "" || !Number.isFinite(n)) return "Enter the credits to apply.";
    if (n < 0) return "Negative values are not allowed.";
    if (n === 0) return "Enter an amount greater than 0.";
    if (n % 5 !== 0) return "Must be a multiple of 5.";
    if (n > balance) return "> available credit amount";
    return null;
  }

  function onCreditBlur() {
    const warning = validateCredit(creditInput);
    setCreditWarning(warning);
    const n = unformat(creditInput);
    if (!warning && Number.isFinite(n)) {
      setCreditInput(
        n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      );
    }
  }

  function onPickCollection(id: string) {
    setCollectionId(id);
    const c = collections.find((x) => x.id === id);
    if (c) setUnitPrice(String(c.priceMax ?? ""));
  }

  function submit() {
    setError(null);
    const onError = (e: unknown) =>
      setError(e instanceof Error ? e.message : "Couldn't add the line.");
    if (mode === "collection") {
      const c = collections.find((x) => x.id === collectionId);
      if (!c) return setError("Pick a collection.");
      const qty = Number(quantity);
      if (!Number.isFinite(qty) || qty <= 0) return setError("Qty must be a positive number.");
      addLine.mutate(
        {
          kind: "collection",
          collectionId,
          description: c.title,
          unitPrice: Number(unitPrice),
          quantity: qty,
        },
        {
          onSuccess: () => {
            setCollectionId("");
            setUnitPrice("");
            setQuantity("1");
          },
          onError,
        },
      );
    } else if (mode === "general") {
      const title = generalTitle.trim();
      if (!title) return setError("Enter a title.");
      const price = unformat(generalUnitPrice);
      if (!Number.isFinite(price) || price < 0) return setError("Unit price must be 0 or more.");
      const qty = Number(generalQty);
      if (!Number.isFinite(qty) || qty <= 0) return setError("Qty must be a positive number.");
      if (price * qty <= 0) return setError("The line total must be greater than 0.");
      addLine.mutate(
        { kind: "general", description: title, unitPrice: price, quantity: qty },
        {
          onSuccess: () => {
            setGeneralTitle("");
            setGeneralUnitPrice("");
            setGeneralQty("1");
          },
          onError,
        },
      );
    } else if (mode === "discount") {
      const d = unformat(discountUsd);
      if (!Number.isFinite(d) || d <= 0) return setError("Enter a discount greater than 0.");
      // Positive magnitude; the server stores it as a negative line and adds it back for credits.
      addLine.mutate(
        { kind: "discount", amount: d },
        { onSuccess: () => setDiscountUsd(""), onError },
      );
    } else {
      // Applied credit is entered in CREDITS and sent as a negative USD line. All four checks
      // (positive, non-negative, multiple of 5, within balance) must pass before the line is added.
      const warning = validateCredit(creditInput);
      if (warning) return setCreditWarning(warning);
      const credits = unformat(creditInput);
      const appliedUsd = creditsPerUsd ? credits / creditsPerUsd : credits;
      addLine.mutate(
        { kind: "applied_credit", amount: -appliedUsd },
        {
          onSuccess: () => {
            setCreditInput("");
            setCreditWarning(null);
          },
          onError,
        },
      );
    }
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
        <TextField
          select
          label="Line"
          value={mode}
          onChange={(e) => setMode(e.target.value as LineMode)}
          size="small"
          sx={{ width: 150, flexShrink: 0 }}
        >
          <MenuItem value="collection">Collection</MenuItem>
          <MenuItem value="general">General</MenuItem>
          <MenuItem value="applied_credit">Applied credit</MenuItem>
          <MenuItem value="discount">Discount</MenuItem>
        </TextField>

        {mode === "collection" ? (
          <>
            <TextField
              select
              label="Collection"
              value={collectionId}
              onChange={(e) => onPickCollection(e.target.value)}
              size="small"
              sx={{ flex: 1, minWidth: 220 }}
            >
              {/* Hide "international" collections from the invoice tool — `category` carries the
                  signatureCollections.type value (see SignatureCollectionService mapping). */}
              {collections
                .filter((c) => c.category !== "international")
                .map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.title}
                  </MenuItem>
                ))}
            </TextField>
            <TextField
              label="Unit price"
              value={unitPrice}
              onChange={(e) => setUnitPrice(e.target.value)}
              size="small"
              type="number"
              sx={{ width: 130, flexShrink: 0 }}
            />
            <TextField
              label="Qty"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              size="small"
              type="number"
              slotProps={{ htmlInput: { min: 1, step: 1 } }}
              sx={{ width: 90, flexShrink: 0 }}
            />
          </>
        ) : mode === "general" ? (
          <>
            <TextField
              label="Title"
              value={generalTitle}
              onChange={(e) => setGeneralTitle(e.target.value)}
              size="small"
              sx={{ flex: 1, minWidth: 220 }}
            />
            <TextField
              label="Unit price"
              value={generalUnitPrice}
              onChange={(e) => setGeneralUnitPrice(e.target.value)}
              size="small"
              type="number"
              sx={{ width: 130, flexShrink: 0 }}
            />
            <TextField
              label="Qty"
              value={generalQty}
              onChange={(e) => setGeneralQty(e.target.value)}
              size="small"
              type="number"
              slotProps={{ htmlInput: { min: 1, step: 1 } }}
              sx={{ width: 90, flexShrink: 0 }}
            />
            <Typography
              sx={{ fontSize: 12.5, color: "var(--color-graphite)", width: 110, flexShrink: 0 }}
            >
              {Number.isFinite(generalTotal) && generalTotal > 0
                ? money(String(generalTotal))
                : "—"}
            </Typography>
          </>
        ) : mode === "discount" ? (
          <Box sx={{ flex: 1, minWidth: 280, display: "flex", alignItems: "center", gap: 1.5 }}>
            <TextField
              label="Discount (USD)"
              value={discountUsd}
              onChange={(e) => setDiscountUsd(e.target.value)}
              onBlur={() => {
                const n = Number(discountUsd.replace(/,/g, ""));
                if (Number.isFinite(n) && n > 0) {
                  setDiscountUsd(
                    n.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }),
                  );
                }
              }}
              size="small"
              type="text"
              slotProps={{ htmlInput: { inputMode: "decimal" } }}
              sx={{ width: 160 }}
            />
            <Typography sx={{ fontSize: 12.5, color: "var(--color-graphite)" }}>
              Reduces the invoice total by this amount. Credits minted are unchanged.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, minWidth: 280, display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <Box>
              <TextField
                label="Credits to apply"
                value={creditInput}
                onChange={(e) => {
                  setCreditInput(e.target.value);
                  if (creditWarning) setCreditWarning(null); // clear while they retype
                }}
                onBlur={onCreditBlur}
                error={creditWarning !== null}
                size="small"
                type="text"
                disabled={balance <= 0}
                slotProps={{ htmlInput: { inputMode: "decimal" } }}
                sx={{ width: 180 }}
              />
              {creditWarning && (
                <Typography sx={{ color: "var(--color-red, #c00)", fontSize: 12, mt: 0.5 }}>
                  {creditWarning}
                </Typography>
              )}
            </Box>
            <Typography sx={{ fontSize: 12.5, color: "var(--color-graphite)", mt: 1 }}>
              {balance.toLocaleString("en-US")} credits available. Must be a positive multiple of 5.
            </Typography>
          </Box>
        )}

        <IconButton
          onClick={submit}
          disabled={addLine.isPending}
          title="Add line"
          aria-label="Add line"
          sx={{
            bgcolor: "var(--color-rodeo-black)",
            color: "var(--color-white)",
            borderRadius: "var(--radius-btn)",
            width: 40,
            height: 40,
            flexShrink: 0,
            "&:hover": { bgcolor: "var(--color-rodeo-black)", opacity: 0.9 },
          }}
        >
          <AddRounded />
        </IconButton>
      </Box>
      {error && (
        <Typography sx={{ color: "var(--color-red, #c00)", fontSize: 12, mt: 0.75 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}

// --- void dialog (type-to-confirm) ----------------------------------------------------------

/** Voiding a finalized invoice is destructive, so we require the user to retype the exact invoice
 *  number (the server re-checks this too). */
/** Sales pre-provision — release the invoice's credits as PROVISIONAL before payment. Reason required. */
function ProvisionCreditsDialog({
  groupId,
  inv,
  open,
  onClose,
}: {
  groupId: number;
  inv: InvoiceWithLines;
  open: boolean;
  onClose: () => void;
}) {
  const provision = useProvisionInvoice(groupId, inv.id);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const reasonValid = reason.trim().length > 0;

  function submit() {
    setError(null);
    provision.mutate(reason.trim(), {
      onSuccess: () => {
        setReason("");
        onClose();
      },
      onError: (e) => setError(e instanceof Error ? e.message : "Couldn't provision credits."),
    });
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>
        Provision credits — {inv.invoiceNumber ?? `#${inv.id}`}
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}
      >
        <Typography sx={{ fontSize: 14, color: "var(--color-graphite)" }}>
          Releases this invoice's credits as <strong>provisional</strong> (spendable) before payment
          is received — they confirm automatically once the invoice is paid. A reason is required.
        </Typography>
        <TextField
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          size="small"
          multiline
          required
          autoFocus
        />
        {error && (
          <Typography sx={{ color: "var(--color-red, #c00)", fontSize: 13 }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!reasonValid || provision.isPending}
          sx={{ bgcolor: "var(--color-rodeo-black)" }}
        >
          {provision.isPending ? "Provisioning…" : "Provision credits"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function VoidInvoiceDialog({
  groupId,
  inv,
  open,
  onClose,
}: {
  groupId: number;
  inv: InvoiceWithLines;
  open: boolean;
  onClose: () => void;
}) {
  const voidInv = useVoidInvoice(groupId, inv.id);
  const [confirm, setConfirm] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const number = inv.invoiceNumber ?? "";
  const matches = confirm.trim() === number;
  const reasonValid = reason.trim().length > 0;

  function submit() {
    setError(null);
    voidInv.mutate(
      { confirmNumber: confirm.trim(), reason: reason.trim() },
      {
        onSuccess: () => {
          setConfirm("");
          setReason("");
          onClose();
        },
        onError: (e) => setError(e instanceof Error ? e.message : "Couldn't void the invoice."),
      },
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1 }}>Void invoice {number}</DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}
      >
        <Typography sx={{ fontSize: 14, color: "var(--color-graphite)" }}>
          Voiding cancels the invoice in Stripe and stops collection. This can't be undone. To
          confirm, type the invoice number <strong>{number}</strong> below.
        </Typography>
        <TextField
          label="Invoice number"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          size="small"
          placeholder={number}
          autoFocus
        />
        <TextField
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          size="small"
          multiline
          required
        />
        {error && (
          <Typography sx={{ color: "var(--color-red, #c00)", fontSize: 13 }}>{error}</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="error"
          variant="contained"
          onClick={submit}
          disabled={!matches || !reasonValid || voidInv.isPending}
        >
          {voidInv.isPending ? "Voiding…" : "Void invoice"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// --- activity log timeline ------------------------------------------------------------------

/** null actorName = a system event (Stripe webhook or the overdue cron). */
const actorLabel = (e: InvoiceEvent) => e.actorName ?? "System";

function ActivityLog({ groupId, invoiceId }: { groupId: number; invoiceId: number }) {
  const { data: events = [], isLoading } = useInvoiceEvents(groupId, invoiceId);

  return (
    <Box sx={{ mt: 4 }}>
      <Typography
        sx={{
          fontSize: 11,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          fontWeight: 600,
          color: "var(--color-tan, #a98d6b)",
          mb: 1.5,
        }}
      >
        Activity
      </Typography>
      {isLoading ? (
        <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>Loading…</Typography>
      ) : events.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
          No activity yet.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {events.map((e, i) => (
            <Box key={e.id} sx={{ display: "flex", gap: 1.5 }}>
              {/* rail: dot + connecting line */}
              <Box
                sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: "5px" }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: "var(--color-rodeo-black)",
                    flexShrink: 0,
                  }}
                />
                {i < events.length - 1 && (
                  <Box sx={{ width: "1px", flex: 1, bgcolor: "var(--color-smoke)", my: "2px" }} />
                )}
              </Box>
              <Box sx={{ pb: 2 }}>
                <Typography sx={{ fontSize: 14, color: "var(--color-rodeo-black)" }}>
                  {e.statusDescription ?? e.eventType}
                  <Box component="span" sx={{ color: "var(--color-graphite)" }}>
                    {" "}
                    — {actorLabel(e)}
                  </Box>
                </Typography>
                <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
                  {dateTime(e.createdAt)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// --- invoice detail / editor ----------------------------------------------------------------

export function InvoiceDetail({
  groupId,
  invoiceId,
  onBack,
  backLabel = "Invoices",
}: {
  groupId: number;
  invoiceId: number;
  onBack: () => void;
  backLabel?: string;
}) {
  const { can } = usePermissions();
  const { data: inv, isLoading } = useInvoice(groupId, invoiceId);
  const finalize = useFinalizeInvoice(groupId, invoiceId);
  const cancel = useCancelInvoice(groupId, invoiceId);
  const removeLine = useRemoveInvoiceLine(groupId, invoiceId);
  const update = useUpdateInvoice(groupId, invoiceId);
  const [actionError, setActionError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [method, setMethod] = useState<ExpectedPaymentMethod | null>(null);
  const [saved, setSaved] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [payoutDrawerOpen, setPayoutDrawerOpen] = useState(false);
  const recordPayout = useRecordInvoicePayout(groupId, invoiceId);

  if (isLoading || !inv) return <Box sx={{ p: 2 }}>Loading…</Box>;

  const isDraft = inv.status === INVOICE_STATUS.draft;
  const isPending = inv.status === INVOICE_STATUS.pending;
  const isOverdue = inv.status === INVOICE_STATUS.overdue;
  const isPartial = inv.status === INVOICE_STATUS.partial_payment;
  // Collectible = still owed money → can record a payment (pending, overdue, OR partially paid).
  const isCollectible = isPending || isOverdue || isPartial;
  // Voidable = finalized, NO payments yet, and nothing minted. Status alone isn't enough: a sales
  // pre-provision releases credits while the invoice is still pending, and voiding then would leave
  // a spendable lot — and the receivable raised behind it — with no invoice to settle them. Reverse
  // the lot first. The server enforces this too (InvoiceAlreadyMintedError).
  const isVoidable = (isPending || isOverdue) && !inv.isMinted;
  // Provisionable = finalized & awaiting payment and NOT yet minted → sales can release credits early.
  const isProvisionable =
    !inv.isMinted && (isPending || isOverdue || inv.status === INVOICE_STATUS.fail_hold);
  const canVoid = can("cancel", "FINANCE");
  // Awaiting Stripe's payout: the client's money cleared at Stripe but the deposit hasn't been seen.
  // stripe_retry counts — the payout bounced and Stripe re-issued it.
  const isAwaitingPayout =
    inv.status === INVOICE_STATUS.clearing || inv.status === INVOICE_STATUS.stripe_retry;
  // Recording an out-of-band payment is a GFG action (clients lack send_invoice) — defense-in-depth;
  // clients don't reach the detail, but never render the money action for them.
  const canRecord = can("send_invoice", "CREDIT");
  const noteVal = note ?? inv.description ?? "";
  const methodVal = method ?? EXPECTED_PAYMENT_METHOD[inv.expectedPaymentMethod] ?? "ach";

  const run = (fn: { mutate: (v: undefined, o: object) => void }, msg: string) => {
    setActionError(null);
    fn.mutate(undefined, {
      onError: (e: unknown) => setActionError(e instanceof Error ? e.message : msg),
    });
  };
  const saveDraft = () => {
    setActionError(null);
    update.mutate(
      { description: noteVal || null, expectedPaymentMethod: methodVal },
      {
        onSuccess: () => {
          setSaved(true);
          setSnackOpen(true);
        },
        onError: (e) => setActionError(e instanceof Error ? e.message : "Couldn't save."),
      },
    );
  };

  return (
    <Box>
      <button
        type="button"
        onClick={onBack}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "var(--color-graphite)",
          fontSize: 14,
          padding: 0,
          marginBottom: 12,
        }}
      >
        <ArrowBack sx={{ fontSize: 18 }} /> {backLabel}
      </button>

      {/* Header: title + status, actions on the right */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 500 }}>
            {inv.invoiceNumber ?? "Draft invoice"}
          </Typography>
          <StatusChip status={inv.status} />
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="outlined" onClick={() => setPreviewOpen(true)}>
            Preview
          </Button>
          {/* Record the BANK deposit — not the client's payment, which is Record payment below.
              Only while the cash is still sitting at Stripe, and only for FINANCE. */}
          {isAwaitingPayout && canVoid && (
            <Button
              variant="outlined"
              startIcon={<PaidOutlined />}
              onClick={() => setPayoutDrawerOpen(true)}
            >
              Record paid
            </Button>
          )}
          {inv.invoicePdf && (
            <Tooltip title="Download PDF" arrow>
              <IconButton
                component="a"
                href={inv.invoicePdf}
                target="_blank"
                rel="noreferrer"
                aria-label="Download PDF"
                sx={{
                  border: "1px solid var(--color-smoke)",
                  borderRadius: "var(--radius-btn, 8px)",
                }}
              >
                <FileDownloadOutlined />
              </IconButton>
            </Tooltip>
          )}
          {isDraft && (
            <>
              {saved && !update.isPending && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: "var(--color-green, #2e7d32)",
                    fontSize: 14,
                    alignSelf: "center",
                  }}
                >
                  <CheckRounded sx={{ fontSize: 18 }} /> Saved
                </Box>
              )}
              <Button variant="outlined" onClick={saveDraft} disabled={update.isPending}>
                Save draft
              </Button>
              <Button
                variant="contained"
                onClick={() => run(finalize, "Couldn't finalize.")}
                disabled={finalize.isPending}
              >
                Finalize & send
              </Button>
            </>
          )}
          {isCollectible && (
            <>
              {inv.hostedInvoiceUrl && (
                <Button
                  variant="outlined"
                  href={inv.hostedInvoiceUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Hosted link
                </Button>
              )}
              {canRecord && (
                <Button variant="contained" onClick={() => setRecordOpen(true)}>
                  Record payment
                </Button>
              )}
            </>
          )}
          {/* Sales pre-provision: release credits before payment (finalized, awaiting payment, unminted). */}
          {canRecord && isProvisionable && (
            <Button variant="outlined" onClick={() => setProvisionOpen(true)}>
              Provision credits
            </Button>
          )}
          {/* Cancel discards a draft; Void kills a finalized invoice with no payments yet. */}
          {isDraft && (
            <Button
              color="error"
              onClick={() => run(cancel, "Couldn't cancel.")}
              disabled={cancel.isPending}
            >
              Cancel
            </Button>
          )}
          {isVoidable && canVoid && (
            <Button color="error" onClick={() => setVoidOpen(true)}>
              Void
            </Button>
          )}
        </Box>
      </Box>

      {inv.cancellationReason && (
        <Typography sx={{ color: "var(--color-red, #c00)", fontSize: 13, mb: 2 }}>
          {inv.status === INVOICE_STATUS.voided ? "Voided" : "Cancelled"}: {inv.cancellationReason}
        </Typography>
      )}

      {/* Finalized invoices show the wallet-style stat block; drafts don't. */}
      {!isDraft && <InvoiceStatBlock inv={inv} />}

      {/* Bill-To on the left; the draft's payment method + note fill the space to its right. */}
      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "stretch", mb: 3 }}>
        <BillToSection groupId={groupId} inv={inv} editable={isDraft} />
        {isDraft && (
          <Box sx={{ flex: 1, minWidth: 280, display: "flex", flexDirection: "column", gap: 1.5 }}>
            <TextField
              select
              label="Expected payment method"
              value={methodVal}
              onChange={(e) => {
                setMethod(e.target.value as ExpectedPaymentMethod);
                setSaved(false);
              }}
              size="small"
              fullWidth
            >
              {EXPECTED_PAYMENT_METHOD.map((m) => (
                <MenuItem key={m} value={m}>
                  {m.toUpperCase()}
                </MenuItem>
              ))}
            </TextField>
            <DueDateField
              value={inv.dueDate ?? ""}
              onChange={(iso) => update.mutate({ dueDate: iso })}
            />
            <TextField
              label="Internal note"
              value={noteVal}
              onChange={(e) => {
                setNote(e.target.value);
                setSaved(false);
              }}
              size="small"
              fullWidth
              multiline
              minRows={2}
            />
          </Box>
        )}
      </Box>

      {/* Add-line control sits above the table for a seamless flow into it. */}
      {isDraft && <AddLineForm groupId={groupId} invoiceId={invoiceId} />}

      <LinesTable inv={inv} editable={isDraft} onRemove={(id) => removeLine.mutate(id)} />

      {/* The credits this invoice will mint on payment (net of any discount add-back) — shown to
          everyone so the credit outcome is explicit while building/reviewing the invoice. */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "baseline",
          gap: 1,
          mt: 1.5,
          pr: 0.5,
        }}
      >
        <Typography sx={{ fontSize: 13.5, color: "var(--color-graphite)" }}>
          Total credits that can be provisioned:
        </Typography>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: "var(--color-rodeo-black)" }}>
          {inv.creditsToMint != null
            ? `${Number(inv.creditsToMint).toLocaleString("en-US")} credits`
            : "—"}
        </Typography>
      </Box>

      {actionError && (
        <Typography sx={{ color: "var(--color-red, #c00)", fontSize: 13, mt: 1 }}>
          {actionError}
        </Typography>
      )}

      {/* Payment receipts — the money-in ledger for this invoice (payments, write-offs, reversals). */}
      {!isDraft && (
        <Box sx={{ mt: 4 }}>
          <Typography
            sx={{
              fontSize: 11,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              fontWeight: 600,
              color: "var(--color-tan, #a98d6b)",
              mb: 1.5,
            }}
          >
            Payments
          </Typography>
          <InvoiceReceipts
            groupId={groupId}
            invoiceId={invoiceId}
            currency={inv.currency}
            isMinted={inv.isMinted}
          />
        </Box>
      )}

      {/* Journal entries before activity: what the books did, then what people did. Hidden for
          drafts, which have posted nothing. GFG-only — the component gates itself. */}
      {!isDraft && <RelatedJournalEntries sourceType={SOURCE_TYPE_INVOICE} sourceId={invoiceId} />}

      {/* Activity log — hidden for drafts (nothing meaningful logged before finalize). */}
      {!isDraft && <ActivityLog groupId={groupId} invoiceId={invoiceId} />}

      <PreviewDrawer
        groupId={groupId}
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        inv={inv}
      />

      {isAwaitingPayout && canVoid && (
        <RecordPayoutDrawer
          subject={`Invoice ${inv.invoiceNumber ?? `#${inv.id}`}`}
          grossUsd={Number(inv.totalAmount ?? 0)}
          currency={(inv.currency ?? "usd").toUpperCase()}
          open={payoutDrawerOpen}
          onClose={() => setPayoutDrawerOpen(false)}
          onSubmit={(payload) => recordPayout.mutateAsync(payload)}
          isPending={recordPayout.isPending}
        />
      )}

      <RecordPaymentDrawer
        groupId={groupId}
        inv={inv}
        open={recordOpen}
        onClose={() => setRecordOpen(false)}
      />

      <VoidInvoiceDialog
        groupId={groupId}
        inv={inv}
        open={voidOpen}
        onClose={() => setVoidOpen(false)}
      />

      <ProvisionCreditsDialog
        groupId={groupId}
        inv={inv}
        open={provisionOpen}
        onClose={() => setProvisionOpen(false)}
      />

      <Snackbar
        open={snackOpen}
        autoHideDuration={2500}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message="Draft saved ✓"
      />
    </Box>
  );
}

// --- panel (blotter ↔ detail) ---------------------------------------------------------------

export default function GroupInvoicesPanel({ groupId }: { groupId: number }) {
  const [selected, setSelected] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<number | null>(null);

  const { data: creators = [] } = useGroupInvoiceCreators(selected == null ? groupId : null);

  if (selected != null) {
    return (
      <InvoiceDetail groupId={groupId} invoiceId={selected} onBack={() => setSelected(null)} />
    );
  }

  const createdByFilter: FilterField = {
    id: "createdBy",
    label: "Created by",
    options: creators.map((c) => ({ value: String(c.id), label: c.name ?? `User #${c.id}` })),
  };

  // Fully server-side: the status pills, Created-by filter, and search/sort/page drive the query.
  const useData = (q: {
    search: string;
    filters: AppliedFilter[];
    sortBy: string;
    sortDir: "asc" | "desc";
    page: number;
    limit: number;
  }): BlotterData<Invoice> => {
    const createdBy = q.filters.find((f) => f.fieldId === "createdBy")?.values ?? [];
    const { data, isLoading } = useInvoices(groupId, {
      status: statusFilter != null ? INVOICE_STATUS_PARAM[statusFilter] : undefined,
      createdBy: createdBy.length ? createdBy.join(",") : undefined,
      search: q.search || undefined,
      sortBy: q.sortBy,
      sortDir: q.sortDir,
      page: q.page,
      limit: q.limit,
    });
    return {
      data: data?.data ?? [],
      total: data?.total ?? 0,
      totalPages: data?.totalPages ?? 1,
      isLoading,
    };
  };

  const config: BlotterConfig<Invoice> = {
    title: "Invoices",
    searchPlaceholder: "Search number or note",
    itemNoun: "invoice",
    rowKey: (i) => i.id,
    defaultSortKey: "created",
    defaultSortDir: "desc",
    filterFields: [createdByFilter],
    useData,
    exportFilename: "invoices.csv",
    exportFetch: async (q) => {
      const createdBy = q.filters.find((f) => f.fieldId === "createdBy")?.values ?? [];
      const res = await fetchInvoices(groupId, {
        status: statusFilter != null ? INVOICE_STATUS_PARAM[statusFilter] : undefined,
        createdBy: createdBy.length ? createdBy.join(",") : undefined,
        search: q.search || undefined,
        page: q.page,
        limit: q.limit,
      });
      return { data: res.data, totalPages: res.totalPages };
    },
    headerAction: (
      <IconButton
        onClick={() => setCreateOpen(true)}
        title="Send invoice"
        aria-label="Send invoice"
        sx={{
          bgcolor: "var(--color-rodeo-black)",
          color: "var(--color-white)",
          borderRadius: "var(--radius-btn)",
          width: 48,
          height: 48,
          "&:hover": { bgcolor: "var(--color-rodeo-black)", opacity: 0.9 },
        }}
      >
        <PostAddOutlined />
      </IconButton>
    ),
    columns: [
      {
        key: "number",
        label: "Invoice",
        sortable: true,
        sortValue: (i) => i.invoiceNumber ?? "",
        render: (i) => (
          <Box>
            <Button
              variant="text"
              size="small"
              sx={{
                textTransform: "none",
                p: 0,
                minWidth: 0,
                fontWeight: 600,
                color: "var(--color-rodeo-black)",
              }}
              onClick={() => setSelected(i.id)}
            >
              {i.invoiceNumber ?? `Draft #${i.id}`}
            </Button>
            {i.description && (
              <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
                {i.description}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        sortValue: (i) => i.status,
        render: (i) => <StatusChip status={i.status} />,
      },
      {
        key: "provisioned",
        label: "Provisioned?",
        sortable: true,
        sortValue: (i) => (i.isMinted ? 1 : 0),
        render: (i) =>
          i.isMinted ? (
            <Tooltip title="Credits provisioned for this invoice" arrow>
              <CheckCircleRounded sx={{ fontSize: 20, color: "var(--color-good-sage, #3a7d44)" }} />
            </Tooltip>
          ) : (
            <Typography component="span" sx={{ color: "var(--color-graphite)", opacity: 0.5 }}>
              —
            </Typography>
          ),
      },
      {
        key: "total",
        label: "Total",
        sortable: true,
        sortValue: (i) => Number(i.totalAmount ?? 0),
        render: (i) => money(i.totalAmount, i.currency),
      },
      {
        key: "net",
        label: "Remaining",
        sortValue: (i) => Number(i.netRemaining ?? 0),
        render: (i) => money(i.netRemaining, i.currency),
      },
      {
        key: "createdBy",
        label: "Created by",
        sortable: true,
        sortValue: creatorName,
        render: (i) => creatorName(i),
      },
      {
        key: "created",
        label: "Created",
        sortable: true,
        sortValue: (i) => i.createdAt,
        render: (i) => dateTime(i.createdAt),
      },
    ],
  };

  return (
    <Box>
      <StatusPills active={statusFilter} onChange={setStatusFilter} />
      <Blotter config={config} />
      <CreateInvoiceDialog
        groupId={groupId}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => setSelected(id)}
      />
    </Box>
  );
}
